import { useMemo } from 'react'
import {
  useHasReceptor,
  useReceptor,
} from 'src/pages/Receptors/receptors.hooks'
import { useCallback } from 'react'
import { ReceptorUtilPromiseClient } from '@trustero/trustero-api-web/lib/receptorutil/receptorutil_grpc_web_pb'
import {
  CreateWorkflowTicketRequest,
  CreateWorkflowTicketResponse,
} from '@trustero/trustero-api-web/lib/receptorutil/receptorutil_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'

export type WorkflowCredentials = {
  Instance?: string
  Site?: string
  Label?: string
  projectId?: string
  site?: string // if the backend sometimes returns lowercase
  label?: string
  [k: string]: unknown
}

export type JiraWorkflowAssigneeOption = {
  accountId: string
  displayName: string
  emailAddress: string
  active: boolean
  avatarUrl: string
}

export const useGetWorkflowCredentials = (
  receptorModelId: string,
): {
  wfCredentials: WorkflowCredentials
} => {
  const { data: HasReceptor } = useHasReceptor(receptorModelId, true)
  const hasWorkflowReceptor = useMemo(
    () => HasReceptor?.getHasReceptor() || false,
    [HasReceptor],
  )

  const { data: receptor } = useReceptor({
    modelId: receptorModelId,
    shouldFetch: hasWorkflowReceptor,
  })

  const wfCredentials = useMemo<WorkflowCredentials>(() => {
    try {
      const raw = receptor?.getCredentials?.()
      if (!raw) return {}
      return JSON.parse(raw) as WorkflowCredentials
    } catch {
      // bad/malformed JSON → treat as empty
      return {}
    }
  }, [receptor])

  return { wfCredentials }
}

export const useCreateWorkflowTicket = (): ((payload: {
  receptorId: string
  projectId: string
  taskTypeId: string
  title: string
  description: string
  label: string
  assigneeId: string
  requestId?: string
}) => Promise<CreateWorkflowTicketResponse>) => {
  const client = useAuthorizedGrpcClient(ReceptorUtilPromiseClient)

  return useCallback(
    async ({
      receptorId,
      projectId,
      taskTypeId,
      title,
      description,
      label,
      assigneeId,
      requestId,
    }) => {
      const request = new CreateWorkflowTicketRequest()
      request.setReceptorId(receptorId)
      request.setProjectId(projectId)
      request.setTaskTypeId(taskTypeId)
      request.setTitle(title)
      request.setDescription(description)
      request.setLabel(label)
      request.setAssigneeId(assigneeId)
      if (requestId) {
        request.setRequestId(requestId)
      }
      return await client.createWorkflowTicket(request)
    },
    [client],
  )
}
